import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  FileText, 
  Send, 
  CheckCircle, 
  Clock, 
  Ban,
  HelpCircle,
  AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';

type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'partial';

interface InvoiceStatusBadgeProps {
  status: InvoiceStatus;
  dueDate?: string | null;
  showTooltip?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

interface StatusConfig {
  label: string;
  icon: React.ElementType;
  className: string;
  description: string;
  nextSteps: string;
}

const STATUS_CONFIG: Record<InvoiceStatus, StatusConfig> = {
  draft: {
    label: 'Draft',
    icon: FileText,
    className: 'bg-muted text-muted-foreground border-muted-foreground/20',
    description: 'This invoice is still being prepared and has not been sent to the customer.',
    nextSteps: 'Finalize and send to customer.',
  },
  sent: {
    label: 'Sent',
    icon: Send,
    className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300',
    description: 'This invoice has been sent to the customer and is awaiting payment.',
    nextSteps: 'Wait for payment or follow up with customer.',
  },
  paid: {
    label: 'Paid',
    icon: CheckCircle,
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    description: 'This invoice has been fully paid.',
    nextSteps: 'No action required. Invoice is complete.',
  },
  partial: {
    label: 'Partial',
    icon: Clock,
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'This invoice has been partially paid.',
    nextSteps: 'Follow up for remaining balance.',
  },
  overdue: {
    label: 'Overdue',
    icon: AlertTriangle,
    className: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
    description: 'This invoice is past its due date and remains unpaid.',
    nextSteps: 'Send payment reminder or follow up urgently.',
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400',
    description: 'This invoice has been cancelled and is no longer valid.',
    nextSteps: 'Create a new invoice if needed.',
  },
};

const getEffectiveStatus = (status: InvoiceStatus, dueDate?: string | null): InvoiceStatus => {
  if (status === 'sent' && dueDate) {
    const dueDateObj = new Date(dueDate);
    if (dueDateObj < new Date()) {
      return 'overdue';
    }
  }
  return status;
};

const InvoiceStatusBadge: React.FC<InvoiceStatusBadgeProps> = ({
  status,
  dueDate,
  showTooltip = true,
  size = 'md',
  className,
}) => {
  const effectiveStatus = getEffectiveStatus(status, dueDate);
  const config = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG.draft;
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-0.5 gap-1.5',
    lg: 'text-sm px-3 py-1 gap-2',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex items-center font-medium border',
        config.className,
        sizeClasses[size],
        className
      )}
    >
      <Icon className={iconSizes[size]} />
      <span>{config.label}</span>
      {showTooltip && (
        <HelpCircle className={cn(iconSizes[size], 'opacity-50')} />
      )}
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          align="start"
          className="max-w-xs p-3"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4" />
              <span className="font-semibold">{config.label}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {config.description}
            </p>
            <div className="pt-1 border-t">
              <p className="text-xs">
                <span className="font-medium">Next: </span>
                {config.nextSteps}
              </p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default InvoiceStatusBadge;
